import React from 'react';

/**
 * Material Symbols Outlined → React component for LehrerDesk.
 * The webfont is preloaded in public/index.html and the base
 * `.material-symbols-outlined` rules live in styles/tokens/base.css.
 *
 * Names are snake_case Material Symbol ids (e.g. "mail", "edit",
 * "auto_awesome", "calendar_today"). Renders a font-glyph <span>.
 *
 * Drop-in for lucide icons: `<MIcon name="edit" size={18} />`.
 * `fill` toggles the filled axis (use for active/selected states).
 */
export interface MIconProps {
  /** snake_case Material Symbol id (e.g. "mail", "more_vert"). */
  name: string;
  /** Glyph size in px (also sets the box width). @default 24 */
  size?: number;
  /** Filled axis — pass true/1 for active/selected states. @default 0 */
  fill?: boolean | number;
  /** Weight axis 100–700. @default 400 */
  weight?: number;
  /** Grade axis. @default 0 */
  grade?: number;
  /** Icon color. @default 'currentColor' */
  color?: string;
  style?: React.CSSProperties;
  className?: string;
  /** Accessible label; when omitted the glyph is aria-hidden (decorative). */
  label?: string;
}

export function MIcon({
  name,
  size = 24,
  fill = 0,
  weight = 400,
  grade = 0,
  color = 'currentColor',
  style,
  className,
  label,
}: MIconProps) {
  const fillVal = fill ? 1 : 0;
  return (
    <span
      className={`material-symbols-outlined${className ? ` ${className}` : ''}`}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
      style={{
        fontSize: size,
        width: size,
        color,
        lineHeight: 1,
        flexShrink: 0,
        fontVariationSettings: `'FILL' ${fillVal}, 'wght' ${weight}, 'GRAD' ${grade}, 'opsz' 24`,
        ...style,
      }}
    >
      {name}
    </span>
  );
}
